import { joinSurveyAction } from '../join-survey';
import type { ConnectChannel } from '../types';

/**
 * Static content for the Audio channel detail page (`/connect/audio`). Same
 * sourcing rule as demographics.ts: copied, not imported, since the source
 * (Data Catalog / EXPLORE_CHANNELS) backs internal-only tooling and this
 * page is visible to every Home user.
 */
export const AUDIO_CHANNEL: ConnectChannel = {
    name: 'Audio',
    icon: 'headphones',
    color: '#639D84',
    subtitle: 'US · Spotify, Pandora, iHeart Radio',
    subtitleLine2: 'Daily · Since 2025',
    exploreDaniPrompt: 'What can audio listening data tell me about my consumers?',
    properties: [
        { name: 'Spotify', letter: 'Sp', color: '#1DB954', icon: 'logos:spotify-icon' },
        {
            name: 'Pandora',
            letter: 'Pa',
            color: '#3668FF',
            icon: 'simple-icons:pandora',
            iconColor: '#3668FF',
        },
        {
            name: 'iHeart Radio',
            letter: 'iH',
            color: '#C6002B',
            icon: 'simple-icons:iheartradio',
            iconColor: '#C6002B',
        },
    ],
    actions: [
        {
            id: 'audio-share-of-ear',
            name: 'Measure share of ear',
            icon: 'headphones',
            summary: 'how your audience splits its listening across music, podcasts and radio',
            template:
                'How does {audience|my audience} split listening across music, podcasts, and radio over {window|the last 12 weeks}?',
        },
        {
            id: 'audio-podcasts',
            name: 'Find podcasts',
            icon: 'mic',
            summary: 'which podcasts your audience listens to most',
            template:
                'Which podcasts does {audience|my target audience} listen to most over {window|the last 12 weeks}?',
        },
        {
            id: 'audio-trends',
            name: 'Track listening',
            icon: 'activity',
            summary: "how your audience's listening is changing over time",
            template:
                'Track listening trends for {audience|my target audience} over {window|the last 12 weeks}',
        },
        joinSurveyAction('audio'),
    ],
    schema: [
        { field: 'consumer_id', type: 'string', description: 'Pseudonymous panel ID' },
        { field: 'app', type: 'string', description: 'Spotify / Pandora / iHeart Radio' },
        { field: 'session_id', type: 'string', description: 'Listening session ID' },
        { field: 'content_type', type: 'string', description: 'music / podcast / radio' },
        { field: 'duration_sec', type: 'number', description: 'Session duration in seconds' },
        { field: 'started_at', type: 'timestamp', description: 'Session start time' },
    ],
    sampleCols: ['App', 'Content', 'Type', 'Duration', 'Date'],
    sampleColWidths: ['minmax(90px, 1fr)', 'minmax(140px, 2fr)', '80px', '80px', '90px'],
    sampleRows: [
        ['Spotify', '"Today\'s Top Hits"', 'music', '1,842s', '2026-04-24'],
        ['Pandora', '"Classic Rock Radio"', 'radio', '2,310s', '2026-04-24'],
        ['iHeart Radio', '"The Breakfast Club"', 'podcast', '3,005s', '2026-04-23'],
        ['Spotify', '"Crime Junkie"', 'podcast', '2,688s', '2026-04-23'],
        ['Pandora', '"Chill Vibes"', 'music', '1,204s', '2026-04-22'],
    ],
};
